import { Capacitor, registerPlugin } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { appLocalStorage } from '../utils/storage';
import { notificationRequest } from './api';

export const isNativeIOS = () => Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
const deviceSettings = registerPlugin('WiwDeviceSettings');
export function createNativePush({ plugin = PushNotifications, native = isNativeIOS, request = notificationRequest,
  settings = deviceSettings, storage = appLocalStorage, onReceived = () => {}, onAction = () => {}, onState = () => {} } = {}) {
  let session = null, generation = 0, disposed = false, pending = null, permissionRequest = null;
  let state = { platform: native() ? 'ios' : 'web', permission: 'unknown', registration: 'unregistered' };
  const publish = patch => { if (!disposed) { state = { ...state, ...patch }; onState(state); } };
  const refreshStatus = async () => {
    if (!native()) return;
    const version = generation;
    try {
      const permission = await plugin.checkPermissions();
      if (version === generation) publish({ permission: permission.receive,
        ...(permission.receive !== 'granted' ? { registration: 'unregistered' } : {}) });
    } catch { if (version === generation) publish({ registration: 'error' }); }
  };
  const handles = [];
  const installation = () => {
    let id = storage.getItem('push-installation-id');
    if (!id) { id = crypto.randomUUID(); storage.setItem('push-installation-id', id); }
    return id;
  };
  const finish = (error, expected = pending) => {
    if (!pending || pending !== expected) return;
    const current = pending; pending = null; clearTimeout(current.timer);
    if (error) { publish({ registration: error.message === 'PUSH_SESSION_CHANGED' ? 'unregistered' : 'error' }); current.reject(error); }
    else { publish({ registration: 'registered' }); current.resolve(); }
  };
  const started = native() ? (async () => {
    const listen = async (event, handler) => {
      const handle = await plugin.addListener(event, handler);
      if (disposed) await handle.remove(); else handles.push(handle);
    };
    await listen('registration', async ({ value }) => {
      const current = session, version = generation, attempt = pending;
      if (!current || disposed) return;
      try {
        const bindingVersion = (Number(storage.getItem('push-binding-version')) || 0) + 1;
        storage.setItem('push-binding-version', String(bindingVersion));
        await request('/devices', { method: 'POST', token: current.token, body: {
          installation_id: installation(), binding_version: bindingVersion, platform: 'ios',
          environment: process.env.REACT_APP_APNS_ENVIRONMENT || 'production', token: value,
        } });
        if (version === generation) finish(undefined, attempt);
      } catch (error) { if (version === generation) finish(error, attempt); }
    });
    await listen('registrationError', () => finish(new Error('PUSH_REGISTRATION_FAILED')));
    await listen('pushNotificationReceived', () => onReceived());
    await listen('pushNotificationActionPerformed', event => {
      const data = event?.notification?.data;
      const id = Number(data?.notification_id), userId = Number(data?.recipient_id);
      if (Number.isSafeInteger(id) && id > 0 && Number.isSafeInteger(userId) && userId > 0) onAction({ id, userId });
    });
  })() : Promise.resolve();
  void started.catch(() => {});

  async function register(prompt = false) {
    if (!native() || disposed) return;
    if (!session) { if (prompt) throw new Error('PUSH_SESSION_CHANGED'); return; }
    const version = generation;
    await started;
    if (disposed || version !== generation) { if (prompt) throw new Error('PUSH_SESSION_CHANGED'); return; }
    let permission = await plugin.checkPermissions();
    if (disposed || version !== generation) { if (prompt) throw new Error('PUSH_SESSION_CHANGED'); return; }
    const previouslyDenied = permission.receive === 'denied';
    if (prompt && ['prompt', 'prompt-with-rationale'].includes(permission.receive)) {
      if (!permissionRequest) permissionRequest = plugin.requestPermissions().finally(() => { permissionRequest = null; });
      permission = await permissionRequest;
    }
    if (disposed || version !== generation) { if (prompt) throw new Error('PUSH_SESSION_CHANGED'); return; }
    publish({ permission: permission.receive });
    if (permission.receive !== 'granted') {
      publish({ registration: 'unregistered' });
      if (storage.getItem('push-installation-id')) await request('/devices', { method: 'DELETE', token: session.token,
        body: { installation_id: installation() } }).catch(() => {});
      if (prompt) throw new Error(previouslyDenied ? 'PUSH_SETTINGS_REQUIRED' : 'PUSH_PERMISSION_DENIED');
      return;
    }
    if (pending) return pending.promise;
    publish({ registration: 'registering' });
    let resolve, reject;
    const promise = new Promise((yes, no) => { resolve = yes; reject = no; });
    const attempt = { promise, resolve, reject, timer: null }; pending = attempt;
    attempt.timer = setTimeout(() => finish(new Error('PUSH_REGISTRATION_FAILED'), attempt), 15000);
    plugin.register().catch(() => finish(new Error('PUSH_REGISTRATION_FAILED'), attempt));
    return promise;
  }
  return {
    async setSession(next) {
      if (next?.token === session?.token && next?.userId === session?.userId) return;
      const previous = session;
      const version = ++generation; finish(new Error('PUSH_SESSION_CHANGED')); session = next;
      publish({ registration: 'unregistered' });
      if (native() && previous && previous.userId !== next?.userId) {
        await plugin.removeAllDeliveredNotifications().catch(() => {});
      }
      if (next && !disposed && version === generation) await register(false);
    },
    register,
    refreshStatus,
    async openSettings() {
      if (!native() || disposed || !session) throw new Error('PUSH_SESSION_CHANGED');
      await settings.openNotifications();
    },
    async dispose() {
      disposed = true; generation++; session = null; finish(new Error('PUSH_SESSION_CHANGED'));
      await started.catch(() => {});
      await Promise.allSettled(handles.map(handle => handle.remove()));
    },
  };
}
